import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Ticket, Sparkles, Users, Calendar, IndianRupee,
  RefreshCw
} from 'lucide-react';
import AdminLayout from '../../components/layouts/AdminLayout';
import { apiClient } from '../../App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import confetti from 'canvas-confetti';

const TicketsPage = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [latestTicket, setLatestTicket] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        apiClient.get('/tickets'),
        apiClient.get('/dashboard/stats')
      ]);
      setTickets(ticketsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const generateTicket = async () => {
    setGenerating(true);
    setLatestTicket(null);
    
    // Simulate suspense
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      const response = await apiClient.post('/tickets/generate');
      setLatestTicket(response.data);
      setTickets([response.data, ...tickets]);
      
      // Confetti celebration
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 }
      });
      
      toast.success(`${response.data.selected_member_name} has been selected!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate ticket');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AdminLayout title="Ticket System" subtitle="Random member selection for fund utilization">
      {/* Generate Ticket Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="dashboard-card p-8 mb-8 text-center"
      >
        <div className="max-w-xl mx-auto">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-[#2962FF] to-[#0A2540] flex items-center justify-center mx-auto mb-6">
            <Ticket className="w-10 h-10 text-white" />
          </div>
          
          <h2 className="text-2xl font-bold text-[#0A2540] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Random Member Selection
          </h2>
          <p className="text-[#425466] mb-6">
            When funds are idle, use this system to fairly select a member for fund utilization.
          </p>

          {stats && (
            <div className="flex justify-center gap-8 mb-8">
              <div className="text-center">
                <p className="text-sm text-[#425466]">Available Balance</p>
                <p className="text-2xl font-bold text-[#00C853]">{formatCurrency(stats.available_balance)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm text-[#425466]">Active Members</p>
                <p className="text-2xl font-bold text-[#0A2540]">{stats.active_members}</p>
              </div>
            </div>
          )}

          <Button
            onClick={generateTicket}
            disabled={generating}
            className="bg-gradient-to-r from-[#2962FF] to-[#0A2540] hover:opacity-90 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
            data-testid="generate-ticket-btn"
          >
            {generating ? (
              <div className="flex items-center gap-3">
                <RefreshCw className="w-6 h-6 animate-spin" />
                Selecting Member...
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Sparkles className="w-6 h-6" />
                Generate Ticket
              </div>
            )}
          </Button>
        </div>
      </motion.div>

      {/* Latest Ticket Result */}
      {latestTicket && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="dashboard-card p-8 mb-8 border-2 border-[#00C853]"
        >
          <div className="text-center">
            <div className="inline-block px-4 py-1 rounded-full bg-[#00C853]/10 text-[#00C853] text-sm font-medium mb-4">
              Latest Selection
            </div>
            <div className="w-24 h-24 rounded-full bg-[#0A2540] flex items-center justify-center mx-auto mb-4 text-white text-3xl font-bold">
              {latestTicket.selected_member_name.charAt(0)}
            </div>
            <h3 className="text-2xl font-bold text-[#0A2540] mb-2" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {latestTicket.selected_member_name}
            </h3>
            <p className="text-[#425466] mb-4">Has been selected for fund utilization</p>
            <div className="flex justify-center gap-8">
              <div>
                <p className="text-sm text-[#425466]">Available Amount</p>
                <p className="text-xl font-bold text-[#00C853]">{formatCurrency(latestTicket.amount)}</p>
              </div>
              <div>
                <p className="text-sm text-[#425466]">Selected On</p>
                <p className="text-xl font-bold text-[#0A2540]">
                  {new Date(latestTicket.created_at).toLocaleDateString('en-IN')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Ticket History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="dashboard-card overflow-hidden"
      >
        <div className="p-6 border-b border-gray-100">
          <h3 className="font-semibold text-[#0A2540]" style={{ fontFamily: 'Poppins, sans-serif' }}>
            Selection History
          </h3>
        </div>
        
        <div className="divide-y divide-gray-100">
          {loading ? (
            [...Array(5)].map((_, i) => (
              <div key={i} className="p-6 flex items-center gap-4">
                <div className="skeleton w-12 h-12 rounded-full"></div>
                <div className="flex-1">
                  <div className="skeleton h-5 w-32 mb-2"></div>
                  <div className="skeleton h-4 w-48"></div>
                </div>
                <div className="skeleton h-6 w-24"></div>
              </div>
            ))
          ) : tickets.length === 0 ? (
            <div className="p-12 text-center text-[#425466]">
              <Ticket className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p className="text-lg mb-2">No tickets generated yet</p>
              <p className="text-sm">Use the button above to generate your first ticket</p>
            </div>
          ) : (
            tickets.map((ticket, index) => (
              <motion.div
                key={ticket.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                className="p-6 flex items-center gap-4 hover:bg-gray-50 transition-colors"
              >
                <div className="w-12 h-12 rounded-full bg-[#0A2540] flex items-center justify-center text-white font-bold">
                  {ticket.selected_member_name.charAt(0)}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[#0A2540]">{ticket.selected_member_name}</p>
                  <div className="flex items-center gap-4 text-sm text-[#425466]">
                    <span className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {new Date(ticket.created_at).toLocaleDateString('en-IN')}
                    </span>
                    <span className="flex items-center gap-1">
                      <IndianRupee className="w-4 h-4" />
                      {formatCurrency(ticket.amount)}
                    </span>
                  </div>
                </div>
                <div className="text-right">
                  <span className="px-3 py-1 rounded-full bg-[#2962FF]/10 text-[#2962FF] text-xs font-medium">
                    #{tickets.length - index}
                  </span>
                </div>
              </motion.div>
            ))
          )}
        </div>
      </motion.div>

      {/* Info Section */}
      <div className="mt-8 p-6 rounded-xl bg-[#F4F7FB] border border-gray-200">
        <h4 className="font-semibold text-[#0A2540] mb-3">How it works</h4>
        <ul className="space-y-2 text-sm text-[#425466]">
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-[#2962FF] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
            <span>When funds are idle in the pool, admin can generate a ticket</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-[#2962FF] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
            <span>System randomly selects an active member from the group</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-[#2962FF] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
            <span>Selected member gets priority for loan or fund utilization</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="w-5 h-5 rounded-full bg-[#2962FF] text-white flex items-center justify-center text-xs flex-shrink-0 mt-0.5">4</span>
            <span>All selections are recorded for complete transparency</span>
          </li>
        </ul>
      </div>
    </AdminLayout>
  );
};

export default TicketsPage;
